import { useEffect } from "react";
import { usePostHog } from "posthog-js/react";
import { Cookie } from "utils/cookieHelper";

/**
 * Custom hook to sync all PostHog feature flags to cookies
 * This makes feature flags available server-side and persists them across sessions
 */
export function useFeatureFlagCookieSync() {
  const posthog = usePostHog();

  useEffect(() => {
    if (!posthog) return;

    const syncFlagsToCookies = () => {
      try {
        // Get all active feature flag names from PostHog's internal storage
        const activeFlagNames = posthog.get_property("$active_feature_flags");

        if (
          !activeFlagNames ||
          !Array.isArray(activeFlagNames) ||
          activeFlagNames.length === 0
        )
          return;

        // Build an object with flag names and their variant values
        const flags = {};
        activeFlagNames.forEach((flagName) => {
          const variant = posthog.getFeatureFlag(flagName);
          if (variant !== undefined) {
            flags[flagName] = variant;
          }
        });

        if (Object.keys(flags).length === 0) return;

        new Cookie().set("posthog_flags", JSON.stringify(flags), {
          path: "/",
          maxAge: 15780000, // ~6 months
        });
      } catch (error) {
        // Silently fail if PostHog is blocked by adblockers or unavailable
        console.warn("Failed to sync PostHog flags to cookies:", error.message);
      }
    };

    if (posthog.featureFlagsReady?.()) {
      syncFlagsToCookies();
    }

    const unsubscribe = posthog.onFeatureFlags(() => {
      syncFlagsToCookies();
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [posthog]);
}

export function getFeatureFlagsFromCookie(cookie) {
  const flagsCookie = cookie.get("posthog_flags");

  if (!flagsCookie) return {};

  try {
    return JSON.parse(flagsCookie);
  } catch (error) {
    console.error("Failed to parse feature flags from cookie:", error);
    return {};
  }
}
